import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NbButtonModule, NbCardModule, NbDialogModule, NbDialogService, NbFormFieldModule, NbInputModule, NbIconModule } from '@nebular/theme';
import { Subject, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { Pessoa } from '../../core/api/pessoa';
import { PessoaService } from '../../core/api/pessoa.service';
import { PessoaFormComponent } from './pessoa-form/pessoa-form.component';
import { PessoaDetalhesDialogComponent } from './pessoa-detalhes-dialog/pessoa-detalhes-dialog.component';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';

import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { Ordenacao, OrdenavelComponent, paraParametroSort } from '../../shared/ordenavel/ordenavel.component';
@Component({
  selector: 'app-pessoa',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbDialogModule,
    NbFormFieldModule,
    NbInputModule,
    NbIconModule,
    ListaVaziaComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './pessoa.component.html',
  styleUrls: ['./pessoa.component.scss'],
})
export class PessoaComponent implements OnInit, OnDestroy {
  /**
   * `TAB-01` — ordenação **no servidor**, porque a listagem é paginada pela API.
   * O estado inicial espelha o padrão do contrato: nome ascendente.
   */
  ordenacao: Ordenacao | null = { campo: 'nome', direcao: 'asc' };

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
    this.page = 1;
    this.carregarPessoas();
  }

  protected get sort(): string | null {
    return paraParametroSort(this.ordenacao);
  }

  pessoas: Pessoa[] = [];
  page = 1;
  size = 10;
  totalPages = 0;
  carregando = false;
  filtroNome = '';
  filtroCpf = '';
  filtroTelefone = '';

  /** `TAB-12` — separa "não há pessoa" de "o recorte atual não casou com nenhuma". */
  get temFiltro(): boolean {
    return !!(this.filtroNome.trim() || this.filtroCpf.trim() || this.filtroTelefone.trim());
  }

  limparFiltros(): void {
    this.filtroNome = '';
    this.filtroCpf = '';
    this.filtroTelefone = '';
    this.page = 1;
    this.carregarPessoas();
  }


  constructor(
    private pessoaService: PessoaService,
    private dialogService: NbDialogService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.carregarPessoas();
    this.route.queryParamMap.pipe(take(1)).subscribe((params) => {
      const editId = params.get('edit')?.trim();
      if (!editId) return;
      this.pessoaService.buscarPorId(editId).subscribe({
        next: (p) => {
          this.editar(p);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });
        },
        error: () => {},
      });
    });
  }

  ngOnDestroy(): void {
  }

  /** `FIL-01` — o filtro vale quando a pessoa manda: Enter ou botão, nunca ao digitar. */
  buscarAgora(): void {
    this.page = 1;
    this.carregarPessoas();
  }

  pesquisar(): void {
    this.buscarAgora();
  }

  carregarPessoas() {
    const n = this.filtroNome.trim();
    const c = this.filtroCpf.trim();
    const t = this.filtroTelefone.trim();
    const usaBusca = !!(n || c || t);
    const req = usaBusca
      ? this.pessoaService.buscaComFiltros(this.page - 1, this.size, n || undefined, c || undefined, t || undefined, this.sort)
      : this.pessoaService.listar(this.page - 1, this.size, '', this.sort);
    this.carregando = true;
    req.pipe(finalize(() => (this.carregando = false))).subscribe({
      next: (res) => {
        this.pessoas = res.content ?? [];
        this.totalPages = res.totalPages || 1;
      },
    });
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.carregarPessoas();
  }

  abrirDialog() {
    this.dialogService.open(PessoaFormComponent).onClose.subscribe((resultado) => {
      if (resultado) this.carregarPessoas();
    });
  }

  editar(pessoa: Pessoa) {
    this.dialogService.open(PessoaFormComponent, { context: { pessoa } }).onClose.subscribe((resultado) => {
      if (resultado) this.carregarPessoas();
    });
  }

  abrirDetalhes(pessoa: Pessoa): void {
    if (!pessoa.id) return;
    this.dialogService.open(PessoaDetalhesDialogComponent, { context: { pessoa } });
  }
}
